import React, { useEffect, useState } from 'react';
import { Route, Routes, BrowserRouter, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './App.css';
import { AppHolder, ThemedGlobalStyle, light } from './settings';
import RootLayout from './components/layout/RootLayout';
import {
  AddressSearch,
  Application,
  ApplicationDetails,
  Code,
  DocumentUploader,
  Impact,
  LandingPage,
  MapView,
  PreLodgementQuestionTemplate,
  PreLodgementReport,
  PromptSelect,
  ReportReview,
  ReviewAndLodge,
  SuccessPage,
} from './modules/pages';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AdminDashboard } from './modules/adminPages';
import { AdminAuthPage } from './modules/adminPages/login';
import { ToastContainer, ToastMessage, ToastWrapper } from './modules/adminPages/login/login.style';
import { LanguageProvider, useLanguage } from './hooks/useLanguageSwitch';
import { IntlProvider } from 'react-intl';
import EnglishContent from './lang/english.json';
import HindiContent from './lang/hindi.json';
import MandarinContent from './lang/mandarin.json';
import VietnameseContent from './lang/vietnamese.json';
import GreekContent from './lang/greek.json';
import PunjabiContent from './lang/punjabi.json';
import { ToastManager } from './components/uielements/toast';
import { InitialEnquireScreen } from './modules/pages/initialEnquireScreen';
import { AnalyticsDashboard } from './modules/adminPages/Analytics';
import { AnalyticsDetailsPage } from './modules/adminPages/AnalyticsDetailsView';
// import TestQuestionComponent from './modules/pages/generateQuestionTemplate/testGenerateQuestion';
import QuestionComponent from './modules/pages/generateQuestionTemplate/generateQuestionNewFlow';

const CustomToast = ({ message }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Immediate navigation based on role
    const userRole = localStorage.getItem('userRole');
    const targetPath = userRole === 'admin' ? '/admin' : '/';

    // Set a small delay for the navigation to ensure toast is visible
    const navigationTimer = setTimeout(() => {
      navigate(targetPath, { replace: true });
    }, 100);

    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, 3000);

    return () => {
      clearTimeout(navigationTimer);
      clearTimeout(hideTimer);
    };
  }, [navigate]);

  if (!isVisible) return null;

  return (
    <ToastWrapper>
      <ToastContainer>
        <ToastMessage>{message}</ToastMessage>
      </ToastContainer>
    </ToastWrapper>
  );
};

const ProtectedRoute = ({ children, authPage }) => {
  const { session } = useAuth();
  const location = useLocation();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const userRole = localStorage.getItem('userRole');
  const isLoggedIn = Boolean(session?.token);

  useEffect(() => {
    if (isLoggedIn) {
      if (location.pathname.startsWith('/admin') && userRole === 'user') {
        setToastMessage('Access denied. Regular users cannot access the admin section.');
        setShowToast(true);
      } else if (!location.pathname.startsWith('/admin') && userRole === 'admin') {
        setToastMessage('Access denied. Admins cannot access user routes.');
        setShowToast(true);
      }
    }
  }, [location.pathname, userRole, isLoggedIn]);

  if (!isLoggedIn) {
    return authPage;
  }

  return (
    <>
      {children}
      {showToast && <CustomToast message={toastMessage} />}
    </>
  );
};

const UserRoutes = () => (
  <Routes>
    <Route path="/" element={<RootLayout />}>
      {/* <Route index element={<InitialEnquireScreen />} /> */}
      <Route index element={<Application />} />
      <Route path="mapView" element={<MapView />} />
      <Route path="promptSelect" element={<PromptSelect />} />
      <Route path="codes" element={<Code />} />
      <Route path="impact" element={<Impact />} />
      <Route path="reviewAndLodge" element={<ReviewAndLodge />} />
      <Route path="reportReview" element={<ReportReview />} />
      <Route path="documentUpload" element={<DocumentUploader />} />
      <Route path="success" element={<SuccessPage />} />
      <Route path="generateQuestion" element={<QuestionComponent />} />
      <Route path="addressSearch" element={<AddressSearch />} />
      <Route path="application/:id" element={<ApplicationDetails />} />
      <Route path="/preLodgementReport" element={<PreLodgementReport />} />
      <Route path="/preLodgementQaChat" element={<PreLodgementQuestionTemplate />} />
      {/* <Route path="/applications" element={<Application />} /> */}
      <Route path="/analytics" element={<AnalyticsDashboard />} />
      <Route path="/analyticsDetails" element={<AnalyticsDetailsPage />} />
    </Route>
  </Routes>
);

const AdminRoutes = () => (
  <Routes>
    <Route path="/" element={<RootLayout />}>
      <Route index element={<AdminDashboard />} />
    </Route>
  </Routes>
);

const Router = () => (
  <Routes>
    <Route
      path="/admin/*"
      element={
        <ProtectedRoute authPage={<AdminAuthPage />}>
          <AdminRoutes />
        </ProtectedRoute>
      }
    />

    <Route
      path="/*"
      element={
        <ProtectedRoute authPage={<LandingPage />}>
          <UserRoutes />
        </ProtectedRoute>
      }
    />

    <Route path="/*" element={<Navigate to="/" replace />} />
  </Routes>
);

const defaultTheme = light;
const queryClient = new QueryClient();

const SecuredApp = () => {
  const lang = useLanguage();

  const PreferredLanguage =
    lang?.selectedLang === 'en-US'
      ? EnglishContent
      : lang?.selectedLang === 'hi-IN'
        ? HindiContent
        : lang?.selectedLang === 'zh-CN'
          ? MandarinContent
          : lang?.selectedLang === 'vi-VN'
            ? VietnameseContent
            : lang?.selectedLang === 'el-GR'
              ? GreekContent
              : lang?.selectedLang === 'pa-IN'
                ? PunjabiContent
                : EnglishContent;

  return (
    <ThemeProvider theme={defaultTheme}>
      <IntlProvider locale={'en-US'} messages={PreferredLanguage}>
        <ThemedGlobalStyle />
        <QueryClientProvider client={queryClient}>
          <AppHolder id="app-global">
            <Router />
          </AppHolder>
        </QueryClientProvider>
        <ToastManager position="top-right" duration={5000} />
      </IntlProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <>
      <AuthProvider>
        <BrowserRouter>
          <LanguageProvider>
            <Routes>
              <Route path="/*" element={<SecuredApp />} />
            </Routes>
          </LanguageProvider>
        </BrowserRouter>
      </AuthProvider>
    </>
  );
}

export default App;
